<template>
  <div class="history">
    <header class="head">
      <h2 class="section-title">Consommation de toute l'infrastructure</h2>
      <RangeTabs :options="RANGES" :value="range" :callback="setRange" label="Période du graphique" />
    </header>
    <div v-if="hasFailed && !history" class="notice">L'historique n'a pas pu être chargé.</div>
    <div v-else-if="!history" class="notice">Chargement…</div>
    <template v-else>
      <p v-if="isThin" class="note">{{ thinNote }}</p>
      <div class="charts">
        <StatisticsCard :span="6" title="Mémoire par container" :subtitle="`En Mo, une courbe par container : chacune se lit seule, sans s'ajouter aux autres (un point toutes les ${bucketText})`" :empty="!hasMemory">
          <label class="toggle"><input type="checkbox" v-model="withHost" /> Ajouter la mémoire utilisée de la machine (axe de droite)</label>
          <StatisticsChart type="line" :labels="labels" :datasets="memoryDatasets" sort-tooltip value-format="integer" height="300px" />
        </StatisticsCard>
        <StatisticsCard :span="6" title="CPU par container" :subtitle="`En % d'un cœur, une courbe par container : chacune se lit seule, sans s'ajouter aux autres (un point toutes les ${bucketText})`" :empty="!hasCpu">
          <StatisticsChart type="line" :labels="labels" :datasets="cpuDatasets" sort-tooltip value-format="percent" height="322px" />
        </StatisticsCard>
      </div>
    </template>
  </div>
</template>

<script>
import StatisticsCard from "@/statistics/components/StatisticsCard.vue";
import StatisticsChart from "@/statistics/components/StatisticsChart.vue";
import RangeTabs from "@/administration/components/RangeTabs.vue";
import {hasData} from "@/statistics/utils/format";
import {AdministrationController} from "@/administration/controllers/AdministrationController";
import {CONTAINER_COLORS, containerColor, dayMinute, megabytes, minute} from "@/administration/utils";

const RANGES = [{value: '1h', label: '1 h'}, {value: '6h', label: '6 h'}, {value: '24h', label: '24 h'}, {value: '7d', label: '7 jours'}, {value: '30d', label: '30 jours'}];
const HOST_COLOR = '#828282';

/**
 * Mémoire et CPU de tous les containers au fil du temps : une courbe par container (non cumulée, pour voir lequel consomme plus que les autres), avec les pics de la période.
 * Les mesures sont prises toutes les minutes par la passerelle et conservées 35 jours.
 * refreshedAt : change quand la page est actualisée, ce qui recharge aussi le graphique ; callback : reçoit l'historique à chaque chargement (le bandeau en tire la consommation actuelle et les pics).
 */
export default {
  name: 'InfraHistory',
  components: {StatisticsCard, StatisticsChart, RangeTabs},
  props: {
    refreshedAt: {type: String, default: ''},
    callback: {type: Function, default: null},
  },
  data() {
    return {
      RANGES,
      range: '24h',
      history: undefined,
      withHost: false,
      hasFailed: false,
      requestId: 0,
    };
  },
  computed: {
    // Short labels for the day, a date and an hour beyond
    labels() {
      const format = ['7d', '30d'].includes(this.history.range) ? dayMinute : minute;
      return this.history.labels.map(format);
    },
    bucketText() {
      const seconds = this.history.bucketSeconds;
      return seconds < 3600 ? `${Math.round(seconds / 60)} min` : `${Math.round(seconds / 3600)} h`;
    },
    hasMemory() {
      return hasData(this.history.total.memoryBytes);
    },
    hasCpu() {
      return hasData(this.history.total.cpuPercent);
    },
    // Fewer than a handful of points : the history has just started
    isThin() {
      return this.history.coverage.samples < 5;
    },
    thinNote() {
      const first = this.history.coverage.firstSampleAt;
      return first
        ? `L'historique se constitue : une mesure par minute, la première date du ${dayMinute(first)}. Le graphique sera plus parlant dans quelques minutes.`
        : "Aucune mesure n'a encore été enregistrée : une mesure est prise chaque minute par le service des utilisateurs. Revenez dans quelques minutes.";
    },
    memoryDatasets() {
      const datasets = this.lines(container => container.memoryBytes, this.history.total.memoryBytes, megabytes);
      if (this.withHost && this.history.host.usedBytes.some(value => value !== null))
        datasets.push({label: 'Machine (utilisée)', data: this.history.host.usedBytes.map(megabytes), type: 'line', color: HOST_COLOR, dashed: true, fill: false, yAxisID: 'y1'});
      return datasets;
    },
    cpuDatasets() {
      return this.lines(container => container.cpuPercent, this.history.total.cpuPercent, value => value);
    },
  },
  watch: {
    refreshedAt() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    setRange(range) {
      this.range = range;
      this.load();
    },
    // One line per container, each from zero (they are not piled up). A container that doesn't answer counts for nothing, where nothing was measured at all there is a gap.
    // The one that uses the most comes first (in the legend and in the tooltip), the color stays the one of the container.
    lines(valuesOf, totals, convert) {
      return this.history.containers
        .map((container, index) => {
          const data = valuesOf(container).map((value, point) => (totals[point] === null ? null : convert(value ?? 0)));
          const measured = data.filter(value => value !== null);
          return {container, index, data, mean: measured.length ? measured.reduce((sum, value) => sum + value, 0) / measured.length : 0};
        })
        .sort((first, second) => second.mean - first.mean)
        .map(({container, index, data}) => ({
          label: container.id,
          data,
          type: 'line',
          color: containerColor(index),
          dashed: index >= CONTAINER_COLORS.length,
          fill: false,
        }));
    },
    async load() {
      // A late answer of an old request must not replace the last one
      const requestId = ++this.requestId;
      const resp = await AdministrationController.getContainersHistory(this.range);
      if (requestId !== this.requestId) return;
      this.hasFailed = !resp || !!resp.errors;
      if (!this.hasFailed) {
        this.history = resp;
        if (this.callback) this.callback(resp);
      }
    },
  },
};
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/statistics/style/statistics.styl';

.history
  margin-top 20px
  margin-bottom 6px

.head
  display flex
  flex-wrap wrap
  align-items center
  justify-content space-between
  gap 8px 16px
  margin-bottom 12px

.section-title
  margin 0
  font-size 17px
  color THEME_COLOR_PRIMARY

.note
  margin 0 0 14px
  padding 8px 12px
  font-size 12.5px
  color #8a5a00
  background #fff6e0
  border-radius 10px

.notice
  padding 28px 16px
  text-align center
  color CTRL_TEXT_MUTED
  background CTRL_BG
  border 1px dashed CTRL_BORDER
  border-radius 12px

.toggle
  display flex
  align-items center
  gap 6px
  margin-bottom 10px
  font-size 12.5px
  color CTRL_TEXT_MUTED
  cursor pointer

  input
    accent-color THEME_COLOR_PRIMARY
</style>
